import { useClubSettings } from "apps/portal/src/providers";
import { DateTime } from "luxon";
import { Dispatch, SetStateAction } from "react";
import { DateRange, DayPicker, Modifiers } from "react-day-picker";

import CustomDropdownNav from "./CustomDropDown";
import DayPickerActions from "./DayPickerActions";
import RangesColumn from "./RangesColumn";

type DatePickerNewProps = {
  range?: DateRange;
  onSelect?: (
    selected: DateRange | undefined,
    triggerDate: Date,
    modifiers: Modifiers,
    e: React.MouseEvent | React.KeyboardEvent,
  ) => void | undefined;
  showOutsideDays?: boolean;
  predefinedRanges?: { label: string; range: { from: Date; to: Date } }[];
  applyRange: (newRange: DateRange) => void;
  onCancel: () => void;
  onApply: () => void;
  displayMonth?: Date;
  onMonthChange?: Dispatch<SetStateAction<Date | undefined>>;
};

const DatePickerNew = ({
  range,
  onSelect,
  showOutsideDays = true,
  predefinedRanges,
  applyRange,
  onCancel,
  onApply,
  displayMonth,
  onMonthChange,
}: DatePickerNewProps) => {
  const { club } = useClubSettings();
  const todayLocal = DateTime.now().setZone(club.timezone);
  const today = todayLocal.startOf("day");

  const todayDate = new Date(today.year, today.month - 1, today.day);
  const startMonth = DateTime.fromObject(
    { year: 2019, month: 1, day: 1 },
    { zone: club.timezone },
  )
    .startOf("month")
    .toJSDate();

  const endMonth = todayLocal
    .plus({ years: 1 })
    .endOf("year")
    .startOf("day")
    .toJSDate();

  return (
    <div className="flex max-w-[20.25rem] flex-col overflow-hidden rounded-[1.25rem] bg-white shadow-lg lg:max-w-none lg:flex-row">
      {predefinedRanges && range && (
        <RangesColumn
          predefinedRanges={predefinedRanges}
          range={range}
          applyRange={applyRange}
        />
      )}
      <div>
        <DayPicker
          month={displayMonth}
          onMonthChange={onMonthChange}
          mode="range"
          selected={range}
          onSelect={onSelect}
          startMonth={startMonth}
          endMonth={endMonth}
          className="p-4"
          captionLayout="dropdown-years"
          classNames={{
            months: "flex justify-center",
            day: "font-medium text-sm hover:rounded-full hover:bg-gray-200",
            selected: "text-gray-950 font-medium bg-gray-100",
            range_start:
              "rounded-full bg-secondary-500 text-white font-medium hover:!bg-secondary-700 !hover:rounded-full after:bg-white",
            range_end:
              "rounded-full bg-secondary-500 text-white font-medium hover:!bg-secondary-700 !hover:rounded-full after:bg-white",
            today:
              "text-grey-950 font-medium relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-secondary-500",
            outside: "text-gray-300",
            nav: "flex absolute z-2 items-center justify-between w-full h-10",
            chevron: "fill-gray-500",
            range_middle: "hover:rounded-none hover:text-gray-700",
          }}
          components={{
            DropdownNav: CustomDropdownNav,
          }}
          modifiers={{
            today: todayDate,
            range_middle: (date) => {
              if (!range?.from || !range?.to) return false;
              return date > range.from && date < range.to;
            },
          }}
          showOutsideDays={showOutsideDays}
          required={false}
        />
        <DayPickerActions onCancel={onCancel} onApply={onApply} />
      </div>
    </div>
  );
};

export default DatePickerNew;
